import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './styles.module.scss';

const History: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);

  // 画像データ（固定）
  const images = [
    { src: '/images/2025/history/2024.jpg', alt: 'かまた祭 2024', href: '/2024' },
    { src: '/images/2025/history/2023.jpg', alt: 'かまた祭 2023', href: '/2023' },
    { src: '/images/2025/history/2022.jpg', alt: 'かまた祭 2022', href: '/2022' },
  ];

  // 無限ループ用の配列を作成
  const infiniteImages = [images[images.length - 1], ...images, images[0]];

  // スクロール処理
  const handleScroll = useCallback(() => {
    if (!carouselRef.current || isScrollingRef.current) return;

    const carousel = carouselRef.current;
    const scrollLeft = carousel.scrollLeft;
    // レスポンシブ対応：画面幅に応じて幅を調整
    const itemWidth = window.innerWidth >= 768 ? window.innerWidth * 0.5 : window.innerWidth * 0.6;
    const newIndex = Math.round(scrollLeft / itemWidth);

    // 通常のインデックス更新
    if (newIndex >= 1 && newIndex <= images.length) {
      setCurrentIndex(newIndex - 1);
    }
  }, [images.length]);

  // スクロール終了時の無限ループ処理
  const handleScrollEnd = useCallback(() => {
    if (!carouselRef.current || isScrollingRef.current || images.length <= 1) return;

    const carousel = carouselRef.current;
    const scrollLeft = carousel.scrollLeft;
    const itemWidth = window.innerWidth >= 768 ? window.innerWidth * 0.5 : window.innerWidth * 0.6;
    const currentPosition = Math.round(scrollLeft / itemWidth);

    // 最初のクローン位置（インデックス0）にいる場合
    if (currentPosition === 0) {
      isScrollingRef.current = true;
      carousel.style.transition = 'none';
      carousel.style.scrollBehavior = 'auto';
      carousel.scrollLeft = images.length * itemWidth;
      setCurrentIndex(images.length - 1);

      void carousel.offsetHeight;

      setTimeout(() => {
        carousel.style.transition = '';
        carousel.style.scrollBehavior = 'smooth';
        isScrollingRef.current = false;
      }, 20);
    }
    // 最後のクローン位置にいる場合
    else if (currentPosition >= images.length + 1) {
      isScrollingRef.current = true;
      carousel.style.transition = 'none';
      carousel.style.scrollBehavior = 'auto';
      carousel.scrollLeft = itemWidth;
      setCurrentIndex(0);

      void carousel.offsetHeight;

      setTimeout(() => {
        carousel.style.transition = '';
        carousel.style.scrollBehavior = 'smooth';
        isScrollingRef.current = false;
      }, 20);
    }
  }, [images.length]);

  // ドットクリック処理
  const handleDotClick = (index: number) => {
    if (!carouselRef.current || isScrollingRef.current) return;

    isScrollingRef.current = true;
    const carousel = carouselRef.current;
    const itemWidth = window.innerWidth >= 768 ? window.innerWidth * 0.5 : window.innerWidth * 0.6;
    carousel.scrollTo({
      left: (index + 1) * itemWidth,
      behavior: 'smooth',
    });
    setCurrentIndex(index);

    setTimeout(() => {
      isScrollingRef.current = false;
    }, 500);
  };

  // 次のスライドへ移動
  const moveToNextSlide = useCallback(() => {
    if (!carouselRef.current || images.length <= 1) return;

    const carousel = carouselRef.current;
    const itemWidth = window.innerWidth >= 768 ? window.innerWidth * 0.5 : window.innerWidth * 0.6;
    const currentScrollLeft = carousel.scrollLeft;
    const currentPosition = Math.round(currentScrollLeft / itemWidth);

    // 最後の実際のイベントを超えた場合は最初に戻る
    if (currentPosition >= images.length) {
      carousel.scrollTo({
        left: itemWidth,
        behavior: 'smooth',
      });
    } else {
      // 次のポジションを計算
      const nextPosition = currentPosition + 1;
      carousel.scrollTo({
        left: nextPosition * itemWidth,
        behavior: 'smooth',
      });
    }
  }, [images.length]);

  // 初期設定
  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel && images.length > 1) {
      // 初期位置を設定（最初の実イメージ = index 1）
      carousel.style.transition = 'none';
      carousel.style.scrollBehavior = 'auto';
      const itemWidth =
        window.innerWidth >= 768 ? window.innerWidth * 0.5 : window.innerWidth * 0.6;
      carousel.scrollLeft = itemWidth;

      void carousel.offsetHeight;

      setTimeout(() => {
        carousel.style.transition = '';
        carousel.style.scrollBehavior = 'smooth';
      }, 20);

      // スクロール終了を検知するためのタイマー
      let scrollEndTimer: NodeJS.Timeout | null = null;

      const handleScrollEvent = () => {
        handleScroll();

        // スクロール終了を検知
        if (scrollEndTimer) clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => {
          handleScrollEnd();
        }, 150);
      };

      carousel.addEventListener('scroll', handleScrollEvent);

      return () => {
        carousel.removeEventListener('scroll', handleScrollEvent);
        if (scrollEndTimer) clearTimeout(scrollEndTimer);
      };
    }
  }, [images.length, handleScroll, handleScrollEnd]);

  // 自動スクロール
  useEffect(() => {
    if (images.length <= 1) return;

    const startAutoScroll = () => {
      autoScrollIntervalRef.current = setInterval(() => {
        moveToNextSlide();
      }, 4000); // 4秒ごとに自動スクロール
    };

    startAutoScroll();

    const carousel = carouselRef.current;
    const handleMouseEnter = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };

    const handleMouseLeave = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      startAutoScroll();
    };

    if (carousel) {
      carousel.addEventListener('mouseenter', handleMouseEnter);
      carousel.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      if (carousel) {
        carousel.removeEventListener('mouseenter', handleMouseEnter);
        carousel.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [images.length, moveToNextSlide]);

  return (
    <div className={styles.history}>
      {/* 背景SVG */}
      <div className={styles.backgroundWrapper}>
        <div className={styles.backgroundSvg}>
          <svg
            width="813"
            height="794"
            viewBox="0 0 813 794"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              width="647.153"
              height="576.917"
              rx="60.1042"
              transform="matrix(0.828688 -0.55971 -0.55971 -0.828688 299.906 817.303)"
              fill="url(#paint0_linear_history)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_history"
                x1="323.576"
                y1="576.917"
                x2="323.576"
                y2="0"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#5087FF" />
                <stop offset="1" stopColor="#0946F1" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <h2 className={styles.title}>HISTORY</h2>
      </div>

      {/* SP/Tablet: カルーセル表示 */}
      <div className={styles.container}>
        <div className={styles.carouselWrapper}>
          <div ref={carouselRef} className={styles.carousel}>
            {infiniteImages.map((image, index) => {
              // 実際のイメージのインデックスを計算（クローンを除外）
              const realIndex =
                index === 0
                  ? images.length - 1
                  : index === infiniteImages.length - 1
                    ? 0
                    : index - 1;
              const isActive = realIndex === currentIndex;

              return (
                <a
                  key={`carousel-${index}`}
                  className={`${styles.carouselItem} ${isActive ? styles.carouselItemActive : ''}`}
                  href={image.href || '#'}
                >
                  <div className={styles.imageCard}>
                    <img src={image.src} alt={image.alt} className={styles.cardImage} />
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        {images.length > 1 && (
          <div className={styles.dots}>
            {images.map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${index === currentIndex ? styles.dotActive : ''}`}
                onClick={() => handleDotClick(index)}
                aria-label={`画像${index + 1}を表示`}
              />
            ))}
          </div>
        )}
      </div>

      {/* PC: 3カラム固定表示 */}
      <div className={styles.desktopGrid}>
        {images.map((image, index) => (
          <a key={`grid-${index}`} className={styles.gridItem} href={image.href || '#'}>
            <div className={styles.imageCard}>
              <img src={image.src} alt={image.alt} className={styles.cardImage} />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default History;
